import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Loader, Paper, Text } from "@mantine/core";
import { Questionnaire, QuestionType } from "src/graphql/generated/graphql";
import { CampaignApplicationRestApi } from "src/types";

import { BarList } from "../components/BarList";
import { ReportSection } from "../components/ReportSection";

export interface ReportQuestionnaireProps {
    questionnaire?: Questionnaire;
    applications: CampaignApplicationRestApi[];
    isLoading?: boolean;
}

// Only finite-option question types are meaningful to chart as distributions.
// (Country has no options in this client, so it is intentionally excluded.)
const CHARTABLE_TYPES: QuestionType[] = [QuestionType.Radio, QuestionType.Checkbox];

// Answers are JSON-parsed at deserialization into the SELECTED OPTION object(s):
// radio = one { key, text } object, checkbox = an array of them. Reduce to option keys
// so they can be joined against the question's option list.
const toKey = (value: unknown): string => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return String((value as { key?: unknown }).key ?? "");
    }
    return String(value);
};

const toValues = (raw: unknown): string[] => {
    if (raw === null || raw === undefined) {
        return [];
    }
    return Array.isArray(raw) ? raw.map(toKey) : [toKey(raw)];
};

export const ReportQuestionnaire = ({ questionnaire, applications, isLoading }: ReportQuestionnaireProps) => {
    const { t } = useTranslation(["common"]);

    const charts = useMemo(() => {
        const questions = (questionnaire?.questions || []).filter(
            (question): question is NonNullable<typeof question> =>
                !!question && !!question.type && CHARTABLE_TYPES.includes(question.type)
        );

        return questions.map((question) => {
            const optionCounts = new Map<string, number>();

            (applications || []).forEach((application) => {
                const answer = application?.questionnaireResponse?.answers?.find((item) => item?.key === question.key);
                toValues(answer?.value as unknown).forEach((value) => {
                    optionCounts.set(value, (optionCounts.get(value) || 0) + 1);
                });
            });

            const items = (question.options || []).map((option) => ({
                label: option?.text || option?.key || "",
                value: optionCounts.get(option?.key || "") || 0,
            }));

            return { key: question.key, text: question.text, items };
        });
    }, [questionnaire?.questions, applications]);

    return (
        <ReportSection title={t("campaign_report_questionnaire")}>
            {isLoading ? (
                <Paper withBorder radius={16} p={24}>
                    <Loader size="sm" />
                </Paper>
            ) : charts.length === 0 ? (
                <Paper withBorder radius={16} p={24}>
                    <Text color="dimmed">{t("campaign_report_no_questionnaire")}</Text>
                </Paper>
            ) : (
                <Grid>
                    {charts.map((chart, index) => (
                        <Grid.Col key={chart.key || index} md={6}>
                            <Paper withBorder radius={16} p={24} style={{ height: "100%" }}>
                                <Text weight={700} color="#0F1B3D">
                                    {chart.text}
                                </Text>
                                <Text mb={20} size="sm" color="dimmed">
                                    {t("campaign_report_responses_hint")}
                                </Text>
                                <BarList items={chart.items} labelWidth={140} />
                            </Paper>
                        </Grid.Col>
                    ))}
                </Grid>
            )}
        </ReportSection>
    );
};
